import * as THREE from 'three';
import { localHour } from '../worldTime';
import { applyWeather, weather } from './weather';

// ── ONE SKY, READ BY EVERYTHING THAT LIGHTS THE STREET ───────────────────────
//
// The old rig had three independent ideas of what time it was: the light
// keyframes (worldTime STOPS), the four Gemini sky panoramas crossfading on their
// own schedule, and a reflection environment made of four fixed lightformers.
// The sun in the panorama was never where the light came from, the reflections
// never had the sky in them, and the fog was one flat colour at every hour.
//
// This is the single source. One hour in, every derived quantity out: where the
// sun is, what colour it is, what the sky looks like around it, what the haze
// does, how bright the windows are, how the picture is graded. The sky shader,
// the light rig, the reflection probe, the post pass and the city's materials all
// read THIS object — so they cannot disagree, because there is nothing to
// disagree with.
//
// Mutated in place every frame (SkyClock), the way beatBridge is: nothing here
// is React state, so nothing re-renders when the sun moves.
//
// The hours keep worldTime's art direction — golden hour peaks at 18.8, dusk is
// 20:00, night falls by 21:12 — so the phase labels, the HUD clock and the
// pre-existing night-dependent features (beat pulse, lamps) still line up.

// Keyframes. Colours are written as you'd pick them (sRGB) and interpolated in
// linear light. `el` is sun elevation in degrees.
//   sun/sunI     the key light while the sun is up
//   zen/hor      sky gradient, zenith → horizon (× skyI)
//   glow         colour of the halo around the sun disc
//   fog/haze     aerial-perspective colour and ground density (per unit)
//   hemi*/amb*   the fill: sky dome above, ground bounce below
//   exp          exposure into the tone curve
//   bloom/thr    how hard bright things glow, and from what brightness
//   gS/gH        grade: tint pushed into shadows / highlights
//   sat/con      grade: saturation and contrast
//   night        0..1, the factor street furniture reads (lamps, windows)
//   cloud        coverage 0..1
const K = [
  { h: 0.0,  el: -38, sun: '#9db4ff', sunI: 0,    zen: '#03060d', hor: '#1a1c2e', skyI: 1.0, glow: '#3a4a80', fog: '#131826', haze: 0.0016, hemiS: '#202c44', hemiG: '#2a1e16', hemiI: 0.42, amb: '#3a4560', ambI: 0.10, exp: 1.25, bloom: 1.25, thr: 0.62, gS: '#0f2a46', gH: '#ffb070', sat: 1.12, con: 1.10, night: 1, stars: 1, cloud: 0.32 },
  { h: 4.8,  el: -20, sun: '#9db4ff', sunI: 0,    zen: '#03060d', hor: '#1a1c2e', skyI: 1.0, glow: '#3a4a80', fog: '#131826', haze: 0.0016, hemiS: '#202c44', hemiG: '#2a1e16', hemiI: 0.42, amb: '#3a4560', ambI: 0.10, exp: 1.25, bloom: 1.25, thr: 0.62, gS: '#0f2a46', gH: '#ffb070', sat: 1.12, con: 1.10, night: 1, stars: 1, cloud: 0.32 },
  { h: 5.9,  el: -5,  sun: '#ff9a7a', sunI: 0,    zen: '#141f45', hor: '#c9788a', skyI: 1.0, glow: '#ff9a7a', fog: '#5e4a6e', haze: 0.0026, hemiS: '#4a5684', hemiG: '#3a2a26', hemiI: 0.45, amb: '#6a6090', ambI: 0.12, exp: 1.15, bloom: 1.0,  thr: 0.70, gS: '#1a2448', gH: '#ffb08a', sat: 1.08, con: 1.06, night: 0.75, stars: 0.35, cloud: 0.36 },
  { h: 6.6,  el: 3,   sun: '#ffaa70', sunI: 1.9,  zen: '#33568f', hor: '#ffc59a', skyI: 1.4, glow: '#ffb070', fog: '#d6ad9a', haze: 0.0022, hemiS: '#9cb2db', hemiG: '#6a5442', hemiI: 0.55, amb: '#b39ac0', ambI: 0.10, exp: 1.02, bloom: 0.75, thr: 0.85, gS: '#22325a', gH: '#ffc89a', sat: 1.10, con: 1.08, night: 0.25, stars: 0, cloud: 0.34 },
  { h: 8.2,  el: 22,  sun: '#ffe8c6', sunI: 3.0,  zen: '#2f6ccc', hor: '#bdd6ee', skyI: 1.55, glow: '#fff4e0', fog: '#bcd0e6', haze: 0.0016, hemiS: '#bccbe0', hemiG: '#9a8268', hemiI: 0.62, amb: '#dbe7f4', ambI: 0.08, exp: 0.95, bloom: 0.55, thr: 0.95, gS: '#20365a', gH: '#ffe8cc', sat: 1.12, con: 1.08, night: 0, stars: 0, cloud: 0.30 },
  { h: 13.0, el: 62,  sun: '#fff2dc', sunI: 3.2,  zen: '#2563c6', hor: '#a9cbe8', skyI: 1.6, glow: '#ffffff', fog: '#b9cde0', haze: 0.0012, hemiS: '#bdcde0', hemiG: '#9c8870', hemiI: 0.62, amb: '#eef4fa', ambI: 0.08, exp: 0.9, bloom: 0.5,  thr: 1.0,  gS: '#1e3456', gH: '#ffeedd', sat: 1.12, con: 1.10, night: 0, stars: 0, cloud: 0.36 },
  { h: 16.2, el: 36,  sun: '#fff0d2', sunI: 3.2,  zen: '#2f6cc8', hor: '#c2d6e6', skyI: 1.55, glow: '#fff0d0', fog: '#c6d2dc', haze: 0.0014, hemiS: '#bccbe0', hemiG: '#9c8466', hemiI: 0.6,  amb: '#f0e8dc', ambI: 0.08, exp: 0.94,  bloom: 0.55, thr: 0.95, gS: '#22365a', gH: '#ffeccc', sat: 1.10, con: 1.10, night: 0, stars: 0, cloud: 0.34 },
  { h: 17.6, el: 18,  sun: '#ffd08a', sunI: 3.1,  zen: '#2f62b0', hor: '#ffe0b4', skyI: 1.45, glow: '#ffc880', fog: '#e2c6a6', haze: 0.0016, hemiS: '#a9bad6', hemiG: '#8a6644', hemiI: 0.56, amb: '#ffd9a0', ambI: 0.06, exp: 1.02, bloom: 0.7,  thr: 0.9,  gS: '#1f3458', gH: '#ffcf8a', sat: 1.14, con: 1.12, night: 0, stars: 0, cloud: 0.38 },
  { h: 18.8, el: 6,   sun: '#ffa152', sunI: 2.8,  zen: '#2c55a2', hor: '#ffcf96', skyI: 1.35, glow: '#ff9a48', fog: '#e8b688', haze: 0.0019, hemiS: '#8ea6c8', hemiG: '#7a4e34', hemiI: 0.50, amb: '#ffd27d', ambI: 0.06, exp: 1.06, bloom: 0.95, thr: 0.82, gS: '#1e3256', gH: '#ffb468', sat: 1.16, con: 1.12, night: 0.05, stars: 0, cloud: 0.42 },
  { h: 19.6, el: 0.5, sun: '#ff7040', sunI: 1.25, zen: '#24387e', hor: '#ff8a5a', skyI: 1.2,  glow: '#ff6a40', fog: '#7a6c86', haze: 0.0016, hemiS: '#5f78a8', hemiG: '#4a3028', hemiI: 0.46, amb: '#9a7a96', ambI: 0.08, exp: 1.12, bloom: 1.05, thr: 0.76, gS: '#1c2a5a', gH: '#ffa070', sat: 1.08, con: 1.10, night: 0.3, stars: 0.05, cloud: 0.42 },
  { h: 20.3, el: -6,  sun: '#ff6a3c', sunI: 0,    zen: '#111a4a', hor: '#7a5a8e', skyI: 1.1, glow: '#b0607a', fog: '#2c2a52', haze: 0.0020, hemiS: '#36447e', hemiG: '#2e2220', hemiI: 0.44, amb: '#6a6a9a', ambI: 0.10, exp: 1.2,  bloom: 1.15, thr: 0.68, gS: '#14224c', gH: '#ffa08a', sat: 1.06, con: 1.10, night: 0.7, stars: 0.4, cloud: 0.38 },
  { h: 21.2, el: -16, sun: '#9db4ff', sunI: 0,    zen: '#04071a', hor: '#22213e', skyI: 1.0, glow: '#4a3a80', fog: '#161a2e', haze: 0.0017, hemiS: '#222e48', hemiG: '#2a1e16', hemiI: 0.42, amb: '#3e4868', ambI: 0.10, exp: 1.25, bloom: 1.25, thr: 0.62, gS: '#0f2a46', gH: '#ffb070', sat: 1.12, con: 1.10, night: 1, stars: 1, cloud: 0.32 },
  { h: 24.0, el: -38, sun: '#9db4ff', sunI: 0,    zen: '#03060d', hor: '#1a1c2e', skyI: 1.0, glow: '#3a4a80', fog: '#131826', haze: 0.0016, hemiS: '#202c44', hemiG: '#2a1e16', hemiI: 0.42, amb: '#3a4560', ambI: 0.10, exp: 1.25, bloom: 1.25, thr: 0.62, gS: '#0f2a46', gH: '#ffb070', sat: 1.12, con: 1.10, night: 1, stars: 1, cloud: 0.32 },
];

const COLOR_KEYS = ['sun', 'zen', 'hor', 'glow', 'fog', 'hemiS', 'hemiG', 'amb', 'gS', 'gH'];
const NUM_KEYS = ['el', 'sunI', 'skyI', 'haze', 'hemiI', 'ambI', 'exp', 'bloom', 'thr', 'sat', 'con', 'night', 'stars', 'cloud'];

// Pre-convert every keyframe colour to linear once.
const KL = K.map((k) => {
  const o = { ...k };
  for (const c of COLOR_KEYS) o[c] = new THREE.Color(k[c]);
  return o;
});

const smooth = (t) => t * t * (3 - 2 * t);

// Where the moon hangs. Fixed rather than computed: a moving moon would swing
// the night shadows around for no gain. On the sun's side of the sky, for the
// same over-the-shoulder reason (see the sun path below).
const MOON_DIR = new THREE.Vector3(0.42, 0.62, 0.66).normalize();

// ── THE SHARED STATE ─────────────────────────────────────────────────────────
export const sky = {
  hour: 18.8,
  phase: '',
  sunEl: 6,
  sunDir: new THREE.Vector3(0, 0.1, -1),
  moonDir: MOON_DIR.clone(),
  // The ONE directional light: the sun while it's up, the moon after.
  lightDir: new THREE.Vector3(0, 1, 0),
  lightColor: new THREE.Color(),
  lightI: 1,
  hemiSky: new THREE.Color(),
  hemiGround: new THREE.Color(),
  hemiI: 0.5,
  ambColor: new THREE.Color(),
  ambI: 0.1,
  fogColor: new THREE.Color(),
  haze: 0.002,
  exposure: 1,
  bloom: 0.6,
  threshold: 0.9,
  gradeShadow: new THREE.Color(),
  gradeHigh: new THREE.Color(),
  saturation: 1.1,
  contrast: 1.1,
  night: 0,
  // Window/sign emissive multiplier: offices dim by day, the city lights up at night.
  glowScale: 1,
  envI: 1,
  // Bumped whenever the sky changes enough that the reflection probe should
  // re-capture (EnvProbe compares against it).
  envVersion: 0,
};

// Uniforms shared BY REFERENCE between every shader that reads the sky — the
// sky dome, the backdrop shell, the reflection capture, the city's lamp pools.
// Writing sky values once updates all of them.
export const skyU = {
  uSunDir: { value: sky.sunDir },
  uMoonDir: { value: sky.moonDir },
  uSunColor: { value: new THREE.Color() },   // halo/disc colour, HDR
  uZenith: { value: new THREE.Color() },
  uHorizon: { value: new THREE.Color() },
  uGround: { value: new THREE.Color() },
  uFogColor: { value: sky.fogColor },
  uNight: { value: 0 },
  uStars: { value: 0 },
  uSunVis: { value: 1 },
  uCloudCover: { value: 0.35 },
  uCloudLit: { value: new THREE.Color() },
  uCloudDark: { value: new THREE.Color() },
  uTime: { value: 0 },
  uCityGlow: { value: new THREE.Color() },   // light pollution low on the horizon at night
};

// ?timelapse=<game minutes per real second> — runs the clock forward from
// wherever it starts, so the whole day can be watched in a couple of minutes.
// Demo and tuning tool; the shared world clock is untouched.
const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
const TIMELAPSE = Math.max(0, parseFloat(qs.get('timelapse')) || 0);

let lastEnvHour = -99;
let lastEnvNight = -1;
const tmpC = new THREE.Color();

/** Evaluate the keyframes at `hour` into `sky` + `skyU`. Cheap: a dozen lerps. */
export function evaluateSky(hour) {
  const h = ((hour % 24) + 24) % 24;
  let i = 0;
  while (i < KL.length - 2 && KL[i + 1].h <= h) i++;
  const a = KL[i]; const b = KL[i + 1];
  const t = smooth(Math.min(1, Math.max(0, (h - a.h) / ((b.h - a.h) || 1))));
  const n = {};
  for (const k of NUM_KEYS) n[k] = a[k] + (b[k] - a[k]) * t;
  const c = (k, out) => out.copy(a[k]).lerp(b[k], t);

  sky.hour = h;
  sky.sunEl = n.el;

  // Sun path: rises in the east (+x), crosses the +z side of the sky, sets in
  // the west (−x).
  //
  // ART DIRECTION OVER ASTRONOMY. The old key sat on the −z side, which is the
  // way the spawn, the fly-in and most first views LOOK — so every face a new
  // player saw was the backlit one, and noon read flat and grey (2026-10-11
  // screenshots). Swung to the other side, the street you arrive on is lit from
  // over your shoulder, the way a cinematographer would set it, and at golden
  // hour the sun still drops straight down the east–west avenues.
  const az = Math.PI * Math.min(1, Math.max(0, (h - 6) / 14));
  const el = THREE.MathUtils.degToRad(n.el);
  sky.sunDir.set(Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el) + 0.18).normalize();

  // ONE directional: the sun above ~−2°, the moon below. Its intensity passes
  // through zero at the hand-over, so swapping the direction never shows.
  const sunUp = THREE.MathUtils.smoothstep(n.el, -2.5, 1.5);
  const moonUp = 1 - THREE.MathUtils.smoothstep(n.el, -6, -1.5);
  if (sunUp > 0.001) {
    sky.lightDir.copy(sky.sunDir);
    c('sun', sky.lightColor);
    sky.lightI = n.sunI * sunUp;
  } else {
    sky.lightDir.copy(MOON_DIR);
    sky.lightColor.set('#a3b6d8');
    sky.lightI = 0.42 * moonUp;
  }

  // The sky already lights everything through the reflection environment
  // (EnvProbe captures it), so the hemisphere is only the extra bounce — at the
  // keyframes' full value the two together washed every shadow lavender.
  c('hemiS', sky.hemiSky); c('hemiG', sky.hemiGround); sky.hemiI = n.hemiI * 0.62;
  c('amb', sky.ambColor); sky.ambI = n.ambI;
  c('fog', sky.fogColor); sky.haze = n.haze;
  sky.exposure = n.exp;
  sky.bloom = n.bloom; sky.threshold = n.thr;
  c('gS', sky.gradeShadow); c('gH', sky.gradeHigh);
  sky.saturation = n.sat; sky.contrast = n.con;
  sky.night = n.night;
  sky.glowScale = 0.35 + 1.45 * n.night;
  // The reflection environment is the sky itself — bright by day, nearly
  // nothing at night, when the street's own lights do the reflecting.
  sky.envI = 0.55 + 0.2 * (1 - n.night);

  // Sky uniforms (HDR, linear).
  c('zen', skyU.uZenith.value).multiplyScalar(n.skyI);
  c('hor', skyU.uHorizon.value).multiplyScalar(n.skyI);
  // Below the horizon the "sky" is the far ground through haze — the fog colour,
  // a touch darker.
  skyU.uGround.value.copy(sky.fogColor).multiplyScalar(0.55);
  c('glow', skyU.uSunColor.value).multiplyScalar(1.0 + 1.6 * (1 - Math.min(1, Math.max(0, n.el / 30))));
  skyU.uNight.value = n.night;
  skyU.uStars.value = n.stars;
  skyU.uSunVis.value = THREE.MathUtils.smoothstep(n.el, -3, 0.5);
  skyU.uCloudCover.value = n.cloud;
  // Clouds take the light of the hour: lit side toward the sun's colour, shadow
  // side toward the zenith. At night they are lit from below by the city.
  tmpC.copy(skyU.uSunColor.value).multiplyScalar(0.55);
  skyU.uCloudLit.value.copy(skyU.uHorizon.value).lerp(tmpC, 0.6 * (1 - n.night)).multiplyScalar(1.15);
  skyU.uCloudDark.value.copy(skyU.uZenith.value).lerp(skyU.uHorizon.value, 0.35).multiplyScalar(0.75);
  skyU.uCityGlow.value.set('#ff8a4a').multiplyScalar(0.10 * n.night);
  if (n.night > 0.5) {
    // Night clouds are the orange sodium underside of a city, not white.
    skyU.uCloudLit.value.lerp(tmpC.set('#3a2c3c'), (n.night - 0.5) * 2);
    skyU.uCloudDark.value.lerp(tmpC.set('#0b0c18'), (n.night - 0.5) * 2);
  }

  // Re-capture reflections every ~6 game-minutes, or on a jump (a ?time pin, a
  // room's pinned hour) — the sky moves too slowly to need more.
  if (Math.abs(h - lastEnvHour) > 0.1 || Math.abs(n.night - lastEnvNight) > 0.05) {
    lastEnvHour = h; lastEnvNight = n.night;
    sky.envVersion += 1;
  }
  return sky;
}

// ── THE CLOCK ────────────────────────────────────────────────────────────────
// Reading the hour costs an Intl format call, so it is sampled twice a second;
// between samples the hour is advanced by the frame clock, so the sun moves
// continuously rather than in half-second steps.
let sampled = null;
let lastEnvRain = 0;
let lastEnvCover = 0;
let lastEnvFlash = false;
let sampledAt = 0;
let lapse = 0;
export function tickSky(timeZone, pin, elapsed, dt) {
  skyU.uTime.value = elapsed;
  const now = performance.now();
  if (sampled == null || now - sampledAt > 500) {
    sampled = localHour(timeZone, pin);
    sampledAt = now;
  }
  if (TIMELAPSE) lapse += (dt * TIMELAPSE) / 60;
  const dev = typeof window !== 'undefined' ? window.__look : null;
  const hour = dev?.hour != null ? dev.hour : sampled + ((now - sampledAt) / 3.6e6) + lapse;
  evaluateSky(hour);
  applyWeather(sky, skyU, sky.hour, Math.min(dt, 0.1), elapsed);
  // Rain and cloud change what shiny things should reflect; re-capture when they move.
  if (Math.abs(weather.rain - lastEnvRain) > 0.08 || Math.abs(weather.cover - lastEnvCover) > 0.08 || (weather.flash > 0.5) !== lastEnvFlash) {
    lastEnvRain = weather.rain; lastEnvCover = weather.cover; lastEnvFlash = weather.flash > 0.5;
    sky.envVersion += 1;
  }
  if (dev && dev.lightScale !== 1) sky.lightI *= dev.lightScale;
  return sky;
}

// Dev-only handle for tuning from the console / a probe: window.__look.sky etc.
// `window.__look.hour = 18.8` pins the clock live; `= null` releases it.
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  window.__look = { sky, skyU, weather, hour: null, lightScale: 1, rain: null };
}

// Evaluate once at import, so anything that reads `sky` before the first frame
// gets a coherent state rather than zeros.
try { evaluateSky(18.8); } catch { /* window-less import */ }
